import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';

const Partners = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [failedLogos, setFailedLogos] = useState(() => new Set());

  // Dynamically load all images from /images/partners folder
  const partnerImages = useMemo(() => {
    try {
      const context = require.context('/public/images/partners', false, /\.(png|webp|jpg|jpeg)$/i);
      return context.keys().map((file) => ({
        name: file.replace(/^\.\//, '').replace(/\.(png|webp|jpg|jpeg)$/i, ''),
        src: `/images/partners/${file.replace(/^\.\//, '')}`
      }));
    } catch (e) {
      console.warn('Could not load partner images from folder', e);
      return [];
    }
  }, []);

  const [emblaRef] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1
    },
    [Autoplay({ delay: 2500, stopOnInteraction: false })]
  );


  const markLogoFailed = (name) => {
    setFailedLogos((prev) => {
      if (prev.has(name)) return prev;
      const next = new Set(prev);
      next.add(name);
      return next;
    });
  };

  return (
    <section id="partners" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-[1.15]">
            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent pb-2">
              {t(language, 'partners.subtitle')}
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t(language, 'partners.description')}
          </p>
        </div>

        {/* Partners Carousel */}
        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-6">
              {partnerImages.map((partner, index) => (
                <div
                  key={index}
                  className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] md:flex-[0_0_33.333%] lg:flex-[0_0_25%]"
                >
                  <div className="group bg-gray-50 rounded-2xl p-8 flex items-center justify-center hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-orange-200 h-32">
                    <div className="w-full h-16 flex items-center justify-center relative overflow-hidden">
                      {failedLogos.has(partner.name) ? (
                        <span className="text-gray-700 font-semibold text-xs text-center px-2">
                          {partner.name.replace(/[-_]/g, ' ')}
                        </span>
                      ) : (
                        <img
                          src={partner.src}
                          alt={partner.name}
                          loading="lazy"
                          decoding="async"
                          className="max-w-full max-h-full object-contain transition-all duration-300"
                          onError={() => markLogoFailed(partner.name)}
                          title={partner.name}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* View All Partners Button */}
        <div className="mt-12 text-center">
          <Button
            onClick={() => navigate('/partners')}
            variant="outline"
            className="border-2 border-[#D98F2C] text-[#D98F2C] hover:bg-[#D98F2C] hover:text-white font-semibold px-8 py-6"
          >
            {t(language, 'partners.viewAll')}
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Partners;


